import { hasCode, isApiError } from '@/api/errors'
import {
  OFFLINE_MESSAGE,
  RATE_LIMITED_MESSAGE,
  startFailure,
  type StartFailure,
} from '@/features/payments/lib/payFailure'
import {
  buildCatalog,
  findPlan,
  isPurchasable,
  offerFor,
  type Catalog,
  type PeriodOffer,
} from '@/features/subscriptions/lib/catalog'
import { formatNPR } from '@/lib/format'
import { sign, type Money } from '@/lib/money'
import {
  knownTierSchema,
  type DiscountPreview,
  type KnownTier,
  type Plan,
} from '@/schemas/subscriptions'
import { periodSpan } from '@/screens/account/components/plans/planGrid'

/**
 * Checkout's decisions, kept out of the components so the loader can share
 * them: which plan the URL names and whether it can be bought, what the
 * summary lists, and what each refusal from mm-be means for the screen.
 */

export type PaidTier = Exclude<KnownTier, 'free'>

/** Why checkout cannot sell the `?plan=` it was given. */
export type UnsellableReason = 'missing' | 'notFound' | 'appOnly' | 'notSold' | 'refused'

export type CheckoutPlan =
  | {
      kind: 'ready'
      plan: Plan
      tier: PaidTier
      catalog: Catalog
      /** Every period the tier is sold for — the "How often" choices. */
      offers: readonly PeriodOffer[]
      /** `plan`'s own period. */
      offer: PeriodOffer
    }
  | { kind: Exclude<UnsellableReason, 'refused'> }

/**
 * The plan behind `?plan=`, or why there is none to sell. Refuses up front
 * whatever mm-be's checkout would refuse — so no Pending row is written for
 * a plan that cannot be bought — plus anything this build cannot name.
 */
export function resolveCheckoutPlan(
  plans: readonly Plan[],
  planId: number | undefined,
): CheckoutPlan {
  if (planId === undefined) return { kind: 'missing' }
  const plan = findPlan(plans, planId)
  if (plan === null) return { kind: 'notFound' }
  if (plan.appOnly) return { kind: 'appOnly' }
  const tier = knownTierSchema.safeParse(plan.tier)
  if (!tier.success || tier.data === 'free' || !isPurchasable(plan)) return { kind: 'notSold' }
  const catalog = buildCatalog(plans)
  const offer = offerFor(catalog, tier.data, plan.periodDays)
  if (offer === null) return { kind: 'notSold' }
  return { kind: 'ready', plan, tier: tier.data, catalog, offers: catalog.tiers[tier.data], offer }
}

/** Every plan the "How often" and AI-signals choices can switch to — what to warm up. */
export function switchablePlanIds(checkout: CheckoutPlan): number[] {
  if (checkout.kind !== 'ready') return []
  return checkout.offers.flatMap((offer) =>
    [offer.base, offer.withSignals].flatMap((plan) => (plan === null ? [] : [plan.id])),
  )
}

/** `offer`'s plan in the variant asked for, or its only one when the period has just that. */
export function variantOf(offer: PeriodOffer, withSignals: boolean): Plan {
  if (withSignals && offer.withSignals !== null) return offer.withSignals
  return offer.base ?? offer.withSignals
}

/** Paisa only when there are any: "Rs 2,490", but "Rs 99.50". */
export const rupees = (amount: Money): string =>
  formatNPR(amount, { whole: /^-?\d+(\.0+)?$/.test(amount) })

export interface SummaryRow {
  label: string
  value: string
}

/**
 * "What it comes to". The first row is the plan before VAT and the VAT row
 * appears only when mm-be charges some (every dev plan says 0) — the API's
 * figure, never a computed 13%. A code's saving and payable are the
 * preview's own; nothing is subtracted here.
 */
export function checkoutSummary(
  plan: Plan,
  tierLabel: string,
  discount: DiscountPreview | null,
): { rows: SummaryRow[]; total: Money } {
  const name = plan.allowSignal ? `${tierLabel} with AI signals` : tierLabel
  const rows: SummaryRow[] = [
    { label: `${name}, ${periodSpan(plan.periodDays)}`, value: rupees(plan.price.amount) },
  ]
  if (sign(plan.price.vat) > 0) rows.push({ label: 'VAT', value: rupees(plan.price.vat) })
  if (discount !== null) {
    rows.push({
      label: `Discount (${discount.discount.code})`,
      value: `−${rupees(discount.discount.amountOff)}`,
    })
  }
  return { rows, total: discount?.discount.payable ?? plan.price.total }
}

export const CODE_REFUSED_MESSAGE = "That code can't be used."
/** mm-be previews a 100%-off code happily, then refuses it at checkout. */
export const CODE_COVERS_ALL_MESSAGE =
  "That code covers the whole price, which can't be paid online."

/** What a refused code says under the field. One message for every kind of bad code, as mm-be gives one code for all of them. */
export function codeFailureMessage(error: unknown, online: boolean): string {
  if (hasCode(error, 'RATE_LIMITED')) return RATE_LIMITED_MESSAGE
  if (isApiError(error) && error.isClientError) return CODE_REFUSED_MESSAGE
  return online ? "Couldn't check that code. Try again." : OFFLINE_MESSAGE
}

export type PayFailure =
  | StartFailure
  /** The plan stopped being sellable since the page loaded. */
  | { kind: 'plan' }
  | { kind: 'code'; text: string }

/**
 * What a refused Pay means for the screen. `withCode`: the request carried a
 * code. The plan was already checked as sellable before Pay, so a
 * `PLAN_NOT_PURCHASABLE` then is mm-be's other use of it — the code covers
 * the whole price — and belongs under the code, not on a dead-end page.
 */
export function payFailure(error: unknown, online: boolean, withCode: boolean): PayFailure {
  if (hasCode(error, 'PLAN_NOT_PURCHASABLE') && withCode) {
    return { kind: 'code', text: CODE_COVERS_ALL_MESSAGE }
  }
  if (hasCode(error, 'PLAN_NOT_PURCHASABLE') || hasCode(error, 'PLAN_NOT_FOUND')) {
    return { kind: 'plan' }
  }
  if (hasCode(error, 'DISCOUNT_NOT_APPLICABLE')) {
    return { kind: 'code', text: CODE_REFUSED_MESSAGE }
  }
  // The gateway, throttle and offline answers every product shares. An error
  // carries one code, so none of them can shadow a plan code above.
  return startFailure(error, online)
}

const CHECKOUT_PATH = '/subscription/checkout'

/** What the router's codec turns a raw query value into: it JSON-parses each one. */
function routerReading(raw: string): string {
  try {
    const value: unknown = JSON.parse(raw)
    if (typeof value === 'string') return value
    return typeof value === 'number' || typeof value === 'boolean' ? String(value) : raw
  } catch {
    return raw
  }
}

/**
 * The code exactly as it was pasted. The router JSON-parses every query
 * value and rewrites the URL on mount, so `?code=1E5` reaches the screen as
 * "100000" and the original text is gone from `location`. The document's
 * own navigation entry still holds the URL the tab was opened with; when its
 * code reads, through the same codec, as the one the router handed over, the
 * raw text is the real one. Anything else — a code the app itself wrote,
 * which the router quotes and never alters — is returned as it came.
 */
export function pastedCode(
  code: string | undefined,
  openedWith: string | undefined,
): string | undefined {
  if (code === undefined || openedWith === undefined) return code
  let opened: URL
  try {
    opened = new URL(openedWith)
  } catch {
    return code
  }
  if (opened.pathname.replace(/\/$/, '') !== CHECKOUT_PATH) return code
  const raw = opened.searchParams.get('code')
  return raw !== null && routerReading(raw) === code ? raw : code
}
